import { ChangeEventHandler, useEffect, useState } from "react";
import translate, { TranslationKey } from "../../functions/translate";
import PasswordSection, { PasswordSectionProps } from "../PasswordSection";
import { Guideline } from '../Guideline'
import { getPasswordErrorMessage, getPasswordErrors, PasswordErrState } from './passwordSchema';
import { GuidelineContainer } from "../GuidelineContainer";

type PasswordRef = PasswordSectionProps['inputRef'];
type PasswordLabel = PasswordSectionProps['label'];
interface NewPasswordProps {
    onPasswordChange: (password: string) => void
    onConfirmPasswordChange?: (password: string) => void
    onPasswordError?: (hasError: boolean) => void
    onConfirmPasswordError?: (hasError: boolean) => void
    onPasswordErrorTypes?: (errs: Set<PasswordErrState>) => void
    passwordRef?: PasswordRef
    confirmPasswordRef?: PasswordRef
    submitAttempted: boolean
    passwordLabel?: PasswordLabel
    confirmPasswordLabel?: PasswordLabel
    showAlreadyUsedError?: boolean
    apiErrorKey?: 'passwordGuidelines' | 'createPassword' | 'enterPassword'
    component: 'yourAccountDetails' | 'forgotPassword' | 'updateAccount' | 'updatePasswordAndSecretQuestion'
}

export const NewPassword = (props: NewPasswordProps) => {

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isValidLength, setIsValidLength] = useState(false);
    const [hasValidCharacters, setHasValidCharacters] = useState(false);
    const [containsNoMbi, setContainsNoMbi] = useState(false);
    const [passwordTouched, setPasswordTouched] = useState(false);
    const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
    const [hasConfirmPasswordError, setHasConfirmPasswordError] = useState(false);
    const [passwordInlineError, setPasswordInlineError] = useState<TranslationKey | null>(null);

    const toggleShowPassword = () => {
        setShowPassword(prev => !prev);
    }

    const { submitAttempted, onPasswordError, onPasswordErrorTypes, onConfirmPasswordError, apiErrorKey, component } = props;

    useEffect(() => {
        const passwordErrors = getPasswordErrors(password);

        const isNotEmpty = Boolean(password.trim().length);

        const validLength = !passwordErrors.has('invalidLength');
        const validChars = !passwordErrors.has('missingOrInvalidCharType');
        const noMbi = isNotEmpty && !passwordErrors.has('containsMBI');

        setIsValidLength(validLength);
        setHasValidCharacters(validChars);
        setContainsNoMbi(noMbi);

        const isValid = passwordErrors.size === 0;

        if (onPasswordError) {
            onPasswordError(!isValid);
        }
        if (onPasswordErrorTypes) {
            onPasswordErrorTypes(passwordErrors);
        }

        // set inline error
        if (!passwordTouched && !submitAttempted) {
            return;
        }

        if (passwordErrors.size > 1) {
            if (isNotEmpty) {
                setPasswordInlineError('check_password_and_update');
            } else if (component === 'yourAccountDetails' || component === 'updatePasswordAndSecretQuestion') {
                setPasswordInlineError('create_password_for_your_account');
            } else {
                setPasswordInlineError('enter_your_new_password');
            }
        } else if (passwordErrors.size === 1) {
            const err = [...passwordErrors.values()][0];
            setPasswordInlineError(getPasswordErrorMessage(err));
        } else if (apiErrorKey === 'passwordGuidelines' || apiErrorKey === 'createPassword') {
            setPasswordInlineError("create_password_for_your_account");
        } else if (apiErrorKey === 'enterPassword') {
            setPasswordInlineError("enter_new_password_different_from_last_six");
        } else {
            setPasswordInlineError(null);
        }

    }, [password, passwordTouched, submitAttempted, onPasswordError, onPasswordErrorTypes, apiErrorKey, component]);

    useEffect(() => {
        const invalidConfirmPassword = (confirmPassword === '' || confirmPassword !== password);
        if (onConfirmPasswordError) {
            onConfirmPasswordError(invalidConfirmPassword);
        }
        // set inline error
        if (!confirmPasswordTouched && !submitAttempted) {
            return;
        }
        setHasConfirmPasswordError(invalidConfirmPassword);
    }, [password, confirmPassword, confirmPasswordTouched, submitAttempted, onConfirmPasswordError]);

    const updatePassword: ChangeEventHandler<HTMLInputElement> = e => {
        const newPassword = e.target.value;
        setPassword(newPassword);
        props.onPasswordChange(newPassword);
    }

    const updateConfirmPassword: ChangeEventHandler<HTMLInputElement> = e => {
        const newPassword = e.target.value;
        setConfirmPassword(newPassword);
        if (props.onConfirmPasswordChange) {
            props.onConfirmPasswordChange(newPassword);
        }
    };

    const guidelines = <GuidelineContainer>
        <Guideline text="must_be_eight_sixteen_char_long" isValid={isValidLength} />
        <Guideline text="must_have_letter_number_symbol" isValid={hasValidCharacters} />
        <Guideline text="not_include_medicare_number" isValid={containsNoMbi} />
    </GuidelineContainer>;

    const alreadyUsedErr = translate("enter_new_password_different_from_last_six");
    const translatedInlineError = passwordInlineError ? translate(passwordInlineError) : null;
    const passwordErr = props.showAlreadyUsedError ? alreadyUsedErr : translatedInlineError;

    return <>
        <div className="ds-u-measure--base ds-u-font-size--base ds-u-margin-top--2">
            {translate("password_cant_be_username_ssn_old_password")}
        </div>
        <PasswordSection
            errorMessage={passwordErr}
            labelClassName="ds-u-margin-top--1 ds-u-measure--wide breakoutText"
            inputRef={props.passwordRef}
            label={props.passwordLabel || translate('password')}
            hint={guidelines}
            onBlur={() => {
                setPasswordTouched(true);
                props.onPasswordChange(password);
            }}
            onChange={updatePassword}
            showPassword={showPassword}
            onShowPasswordChange={toggleShowPassword}
            value={password}
        />
        <PasswordSection
            errorMessage={hasConfirmPasswordError ? translate('reenter_new_password') : null}
            inputRef={props.confirmPasswordRef}
            label={props.confirmPasswordLabel || translate('confirm_password')}
            name="confirm password"
            onBlur={() => {
                setConfirmPasswordTouched(true);
                if (props.onConfirmPasswordChange) {
                    props.onConfirmPasswordChange(confirmPassword);
                }
            }}
            onChange={updateConfirmPassword}
            showPassword={showPassword}
            onShowPasswordChange={toggleShowPassword}
            value={confirmPassword}
        />
    </>;
};
